import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Picture from './Picture';
import { getOptionImageUrl, getOptionTypeLabel } from '../utils/cmdbOptionAttributes';

function PictureSelectionModal({
  isOpen,
  onClose,
  currentField,
  options,
  selections,
  onSelect,
  onSubmit,
  picturesData,
  multiFields,
}) {
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const uniqueTypes = useMemo(() => {
    const types = options
      .map(option => getOptionTypeLabel(option))
      .filter(Boolean);
    return [...new Set(types)];
  }, [options]);

  const typeOptions = useMemo(
    () =>
      uniqueTypes.map(type => ({
        value: type,
        label: type
      })),
    [uniqueTypes]
  );

  useEffect(() => {
    setSearchTerm('');
  }, [activeFilters]);

  useEffect(() => {
    if (!isOpen) {
      setActiveFilters([]);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    let filtered = options;

    if (activeFilters.length) {
      filtered = filtered.filter(option => activeFilters.includes(getOptionTypeLabel(option)));
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [options, activeFilters, searchTerm]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 focus:outline-none"
            aria-label="Fermer"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h2 className="text-lg font-medium">Sélection {currentField.name}</h2>
          {currentField && (currentField.jiraSchema.custom === 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect'
            || multiFields.includes(currentField.fieldId)) && (
            <p className="text-sm text-gray-500 mt-1">Vous pouvez sélectionner plusieurs éléments</p>
          )}

          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Filtrer par type</p>
            <Select
              isMulti
              isClearable
              options={typeOptions}
              value={typeOptions.filter(option => activeFilters.includes(option.value))}
              onChange={(selected) => setActiveFilters((selected || []).map(option => option.value))}
              placeholder="Sélectionner un ou plusieurs types…"
              className="text-sm"
              classNamePrefix="picture-select"
            />
          </div>

          <div className="mt-4 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom ou EAN..."
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Effacer la recherche"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 py-4">
            {filteredOptions.map(option => {
              const isSelected = (selections || []).some(item => item.value === option.value);

              return (
                <button
                  key={option.value}
                  onClick={() => onSelect(option)}
                  className={`p-4 border rounded-lg flex flex-col items-center gap-2 transition-colors
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500 ring-opacity-50'
                      : 'hover:bg-blue-100'}`}
                >
                  <div className="relative">
                    <Picture label={option.label} pictures={picturesData} url={getOptionImageUrl(option)} />

                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-700">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          {currentField && (currentField.jiraSchema.custom === 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect'
            || currentField.fieldId === 'customfield_10102') && (
            <button
              onClick={onSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Valider ({selections.length} sélectionné{selections.length > 1 ? 's' : ''})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PictureSelectionModal;